import {
  Anchor,
  Box,
  Button,
  Card,
  Group,
  Image,
  SimpleGrid,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import {
  IconArrowRight,
  IconBook2,
  IconCalendarEvent,
  IconMessageDots,
  IconPlus,
  IconShoppingCart,
} from '@tabler/icons-react';
import { type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { FORM_TEMPLATES, type FormTemplate } from '../../lib/form-templates';
import { useRustyState } from '../../state';

const TEMPLATE_ICONS: Record<FormTemplate['iconName'], ReactNode> = {
  'shopping-cart': <IconShoppingCart size={20} />,
  feedback: <IconMessageDots size={20} />,
  event: <IconCalendarEvent size={20} />,
};

function DecorativeCard() {
  return (
    <Box
      visibleFrom="lg"
      style={{
        position: 'absolute',
        bottom: -24,
        left: -32,
        width: 192,
        height: 128,
        background: 'var(--mantine-color-body)',
        border: '1px solid var(--mantine-color-default-border)',
        borderRadius: 8,
        boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04), 0 1px 2px -1px rgba(0,0,0,0.04)',
        transform: 'rotate(-6deg)',
        padding: 12,
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 168 104"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <rect
          x="0"
          y="6"
          width="112"
          height="6"
          rx="3"
          fill="var(--mantine-color-default-border)"
        />
        <rect x="0" y="28" width="168" height="6" rx="3" fill="var(--mantine-color-gray-2)" />
        <rect x="0" y="50" width="84" height="6" rx="3" fill="var(--mantine-color-gray-2)" />
        <rect x="0" y="72" width="140" height="6" rx="3" fill="var(--mantine-color-gray-2)" />
      </svg>
    </Box>
  );
}

export function AccountHomePage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();
  const forms = useRustyState((state) => state.forms);

  useEffect(() => {
    useRustyState.getState().getAndSetVerifiedEmails();
    useRustyState.getState().getAndSetForms();
  }, []);

  const hasForms = forms.length > 0;

  const handleTemplateClick = (template: FormTemplate) => {
    nav(createLanguageURL(`/account/forms/create?template=${template.id}`));
  };

  return (
    <Box py="xl">
      <Stack align="center" gap="lg" maw={720} mx="auto" px="md">
        <Box pos="relative" mb="md" style={{ display: 'flex', justifyContent: 'center' }}>
          <Box
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: -1,
            }}
          >
            <Box
              w={256}
              h={256}
              style={{
                borderRadius: '50%',
                background: 'var(--mantine-primary-color-light)',
                opacity: 0.4,
                filter: 'blur(48px)',
              }}
            />
          </Box>

          <Box pos="relative" w={288} h={192} style={{ transform: 'rotate(2deg)' }}>
            <Card
              withBorder
              radius="md"
              p={0}
              shadow="sm"
              style={{ width: '100%', height: '100%', overflow: 'hidden' }}
            >
              <Image
                src="/dashboard-hero.jpg"
                alt={t('account.heroAlt')}
                w="100%"
                h="100%"
                fit="cover"
                style={{ filter: 'grayscale(1)', opacity: 0.85, mixBlendMode: 'multiply' }}
              />
            </Card>
            <Box
              style={{
                position: 'absolute',
                top: -16,
                right: -16,
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'var(--mantine-primary-color-filled)',
                color: 'var(--mantine-color-white)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 16px -4px rgba(0,0,0,0.15)',
              }}
            >
              <IconPlus size={24} />
            </Box>
          </Box>

          <DecorativeCard />
        </Box>

        {!hasForms && (
          <Stack align="center" gap="sm" px="md">
            <Title order={1} ta="center" fw={600}>
              {t('account.heroTitle')}
            </Title>
            <Text size="lg" c="dimmed" ta="center" maw={520}>
              {t('account.heroSubtitle')}
            </Text>
          </Stack>
        )}

        <Stack align="center" gap="xs">
          <Group gap="md" wrap="wrap" justify="center">
            <Button
              component={Link}
              to={createLanguageURL('/account/forms/create')}
              size="md"
              leftSection={<IconPlus size={18} />}
            >
              {t('account.createNewForm')}
            </Button>
            <Button
              component={Link}
              to={createLanguageURL('/account/docs')}
              size="md"
              variant="default"
              leftSection={<IconBook2 size={18} />}
            >
              {t('account.readTheDocs')}
            </Button>
          </Group>
          {hasForms && (
            <Anchor component={Link} to={createLanguageURL('/account/forms')} size="sm" c="dimmed">
              <Group gap={4} align="center" wrap="nowrap">
                {t('account.goToForms')}
                <IconArrowRight size={14} />
              </Group>
            </Anchor>
          )}
        </Stack>

        <Group gap="md" align="center" w="100%" justify="center" mt="lg" opacity={0.6}>
          <Box h={1} w={48} bg="var(--mantine-color-default-border)" />
          <Text size="xs" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.2em' }}>
            {t('account.orTryATemplate')}
          </Text>
          <Box h={1} w={48} bg="var(--mantine-color-default-border)" />
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" w="100%">
          {FORM_TEMPLATES.map((template) => (
            <UnstyledButton key={template.id} onClick={() => handleTemplateClick(template)}>
              <Card withBorder radius="md" p="md" shadow="xs" style={{ height: '100%' }}>
                <Stack gap={6}>
                  <Box c="var(--mantine-primary-color-filled)">
                    {TEMPLATE_ICONS[template.iconName]}
                  </Box>
                  <Text fw={700} size="sm">
                    {t(template.titleKey)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {`${t(template.descriptionKey)} →`}
                  </Text>
                </Stack>
              </Card>
            </UnstyledButton>
          ))}
        </SimpleGrid>
      </Stack>
    </Box>
  );
}
